import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  ViewChild,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
  selector: 'app-dynamic-infomodal',
  standalone: true,
  imports: [],
  templateUrl: './dynamic-infomodal.component.html',
  styleUrl: './dynamic-infomodal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class DynamicInfomodalComponent implements OnDestroy {
  private readonly sanitizer = inject(DomSanitizer);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly document = inject(DOCUMENT);

  @ViewChild('backdrop') backdropRef?: ElementRef<HTMLElement>;

  @Input() modalTitle = 'Information';
  @Input() label = '';

  @Input()
  set infoText(value: string) {
    this._infoText = value || '';
    this.cachedHtml = this.sanitizer.bypassSecurityTrustHtml(this._infoText);
  }
  get infoText(): string {
    return this._infoText;
  }

  private _infoText = '';
  cachedHtml: SafeHtml = this.sanitizer.bypassSecurityTrustHtml('');
  isOpen = false;
  private savedBodyOverflow = '';

  open(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.isOpen) {
      return;
    }
    this.isOpen = true;
    this.cdr.detectChanges();
    this.mountBackdropInViewport();
  }

  close(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (!this.isOpen) {
      return;
    }
    this.isOpen = false;
    this.restoreBodyScroll();
    this.cdr.markForCheck();
  }

  onBackdropClick(event: MouseEvent): void {
    if (
      (event.target as HTMLElement).classList.contains(
        'dynamic-infomodal-backdrop'
      )
    ) {
      this.close(event);
    }
  }

  private mountBackdropInViewport(): void {
    const backdrop = this.backdropRef?.nativeElement;
    if (!backdrop) {
      return;
    }

    if (backdrop.parentElement !== this.document.body) {
      this.document.body.appendChild(backdrop);
    }

    backdrop.style.position = 'fixed';
    backdrop.style.inset = '0';
    backdrop.style.width = '100vw';
    backdrop.style.height = '100vh';
    backdrop.style.zIndex = '10000';
    backdrop.style.display = 'flex';
    backdrop.style.alignItems = 'center';
    backdrop.style.justifyContent = 'center';

    this.savedBodyOverflow = this.document.body.style.overflow;
    this.document.body.style.overflow = 'hidden';
  }

  private restoreBodyScroll(): void {
    this.document.body.style.overflow = this.savedBodyOverflow;
  }

  ngOnDestroy(): void {
    this.restoreBodyScroll();
    const backdrop = this.backdropRef?.nativeElement;
    if (backdrop?.parentElement === this.document.body) {
      backdrop.remove();
    }
  }
}
